// Martian Bridge -- the missing INSIGHT -> RECOMMENDATION -> ACTION PLAN ->
// APPROVAL write surface for the Brand Growth OS.
//
// Phase 0 finding: OpportunityPortfolioPage already showed real
// opportunities, and BrandCommandCenterPage already showed a real
// "next best action", but neither page (nor any other page in the
// frontend) ever called RecommendationService's or ActionPlanService's
// real decision routes (detect/acknowledge/approve/reject/dismiss/cancel)
// -- `intelligenceApi.js`'s `recommendationsAPI`/`actionPlansAPI` wrapped
// only `listForEntity`/`get` before this change. The backend chain was
// fully real, RBAC-gated (editor to detect/update, admin to
// approve/reject/dismiss/cancel), tenant-scoped, and even audited
// (approve_action_plan_route records a security-audit event) -- it was
// simply never reachable from any UI. This panel is the bridge: it
// reuses those exact routes verbatim, introduces no new backend
// behavior, and renders only fields the backend actually returns.
//
// Martian never executes anything here -- approving a recommendation or an
// action plan is a human decision recorded for execution elsewhere
// (ActionPlanService's own docstring: "never calls any external
// platform"). No execution status, no fabricated outcome, no invented
// metric is ever shown.

import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { EmptyState, ErrorState, LoadingState } from "./StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { recommendationsAPI, actionPlansAPI } from "@/lib/intelligenceApi";
// AF UX audit P0 slice (docs/product/intelligence-console-ux-audit.md,
// Section 8/20) -- the recommendation decision card and its action-plan
// section moved to their own shared file so RecommendationsPage.js can
// reuse the exact same decision UI/RBAC gating instead of a second,
// divergent implementation. Byte-identical behavior here: only the
// import site changed.
import { RecommendationDecisionCard, CAN_GENERATE_PLAN_FROM, REC_DECISION_LABEL, errorDetail } from "./RecommendationDecisionCard";

export function RecommendationBridgePanel({ open, onOpenChange, workspaceId, opportunity }) {
  const { isAdmin, isEditor } = useAuth();
  const [recommendations, setRecommendations] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [detectingRecs, setDetectingRecs] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [plansByRec, setPlansByRec] = useState({});
  const [plansLoadingFor, setPlansLoadingFor] = useState({});
  const [detectingPlanFor, setDetectingPlanFor] = useState(null);
  const [decidingPlanId, setDecidingPlanId] = useState(null);

  const opportunityId = opportunity?.id;

  const loadPlansFor = useCallback((rec) => {
    if (!CAN_GENERATE_PLAN_FROM.has(rec.status)) return;
    setPlansLoadingFor((m) => ({ ...m, [rec.id]: true }));
    actionPlansAPI.byRecommendation(workspaceId, rec.id)
      .then((r) => setPlansByRec((m) => ({ ...m, [rec.id]: r.data?.action_plans || [] })))
      .catch(() => setPlansByRec((m) => ({ ...m, [rec.id]: [] })))
      .finally(() => setPlansLoadingFor((m) => ({ ...m, [rec.id]: false })));
  }, [workspaceId]);

  const load = useCallback(() => {
    if (!open || !workspaceId || !opportunityId) return;
    setError(null);
    setRecommendations(null);
    recommendationsAPI.forOpportunity(workspaceId, opportunityId)
      .then((r) => {
        const items = r.data?.recommendations || [];
        setRecommendations(items);
        items.forEach(loadPlansFor);
      })
      .catch((e) => setError(e));
  }, [open, workspaceId, opportunityId, loadPlansFor]);

  useEffect(() => { load(); }, [load]);

  const handleDetectRecommendations = () => {
    setDetectingRecs(true);
    recommendationsAPI.detect(workspaceId, opportunityId)
      .then(() => load())
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDetectingRecs(false));
  };

  const handleDecide = (recommendation, action) => {
    setDecidingId(recommendation.id);
    recommendationsAPI[action](workspaceId, recommendation.id)
      .then((r) => {
        setRecommendations((list) => list.map((x) => (x.id === recommendation.id ? r.data : x)));
        toast.success(`Recommendation ${REC_DECISION_LABEL[action].toLowerCase()}d.`);
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDecidingId(null));
  };

  const handleDetectPlan = (recommendation) => {
    setDetectingPlanFor(recommendation.id);
    actionPlansAPI.detect(workspaceId, recommendation.id)
      .then((r) => {
        setPlansByRec((m) => ({ ...m, [recommendation.id]: r.data?.action_plans || [] }));
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDetectingPlanFor(null));
  };

  const handleDecidePlan = (recommendation, plan, action) => {
    setDecidingPlanId(plan.id);
    actionPlansAPI[action](workspaceId, plan.id)
      .then((r) => {
        setPlansByRec((m) => ({
          ...m,
          [recommendation.id]: (m[recommendation.id] || []).map((p) => (p.id === plan.id ? r.data : p)),
        }));
        toast.success(action === "approve" ? "Action plan approved." : "Action plan cancelled.");
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDecidingPlanId(null));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="recommendation-bridge-panel">
        <SheetHeader>
          <SheetTitle className="text-[var(--text-primary)] text-[14px]">Recommendations &amp; action plan</SheetTitle>
          <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">
            {opportunity?.opportunity_type?.replace(/_/g, " ") || "This opportunity"} -- what Martian recommends, and what's ready to act on.
          </SheetDescription>
        </SheetHeader>

        <Separator className="my-3 bg-[var(--border)]" />

        {error && <ErrorState error={error} onRetry={load} testId="recommendation-bridge-error" />}

        {!error && recommendations === null && <LoadingState testId="recommendation-bridge-loading" />}

        {!error && recommendations !== null && recommendations.length === 0 && (
          isEditor ? (
            <EmptyState
              testId="recommendation-bridge-empty"
              title="NO RECOMMENDATIONS YET"
              description="Martian has not generated a recommendation for this opportunity yet."
              actionLabel={detectingRecs ? "Asking Martian…" : "Ask Martian for a recommendation"}
              onAction={detectingRecs ? undefined : handleDetectRecommendations}
            />
          ) : (
            <EmptyState
              testId="recommendation-bridge-empty"
              title="NO RECOMMENDATIONS YET"
              description="Martian has not generated a recommendation for this opportunity yet. An editor can ask Martian to generate one."
            />
          )
        )}

        {!error && recommendations !== null && recommendations.length > 0 && (
          <div className="space-y-2.5" data-testid="recommendation-list">
            {recommendations.map((rec) => (
              <RecommendationDecisionCard
                key={rec.id}
                recommendation={rec}
                isAdmin={isAdmin}
                isEditor={isEditor}
                onDecide={handleDecide}
                decidingId={decidingId}
                onDetectPlan={handleDetectPlan}
                detectingPlanFor={detectingPlanFor}
                plans={plansByRec[rec.id]}
                plansLoading={!!plansLoadingFor[rec.id]}
                onDecidePlan={handleDecidePlan}
                decidingPlanId={decidingPlanId}
              />
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
